import { Alert, Box, Button, IconButton } from "@mui/material";
import { formatInTimeZone } from "date-fns-tz/fp";
import { isValid, parse } from "date-fns/fp";
import {
  always,
  concat,
  equals,
  filter,
  fromPairs,
  identity,
  map,
  pipe,
  propOr,
  reject,
  toPairs,
  trim,
  uniq,
} from "ramda";
import {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useReducer,
  useRef,
  useState,
} from "react";
import {
  FormProvider,
  useFieldArray,
  useForm,
  useFormContext,
} from "react-hook-form";
import { getCoachLanguagesOptions } from "../../../components/Forms";
import {
  AutocompleteSelect,
  BareInputField,
  DatePickerField,
  FileUpload,
  SwitchField,
} from "../../../components/Forms/Fields";
import { useServerFieldErrors } from "../../../components/Forms/parametrizedValidate";
import { Icon } from "../../../components/Icon";
import { useRightMenu } from "../../../components/Layout";
import { generalMessages } from "../../../components/messages";
import { Msg } from "../../../components/Msg";
import { useMsg } from "../../../components/Msg/Msg";
import { ScrollableRightMenu } from "../../../components/ScrollableRightMenu";
import { H2, P } from "../../../components/Typography";
import { useStaticCallback } from "../../../hooks/useStaticCallback.hook";
import { useAuth } from "../../Authorization";
import { useMyMutation, useMyQuery } from "../../Authorization/AuthProvider";
import { IntroLink } from "../../Coaches/IntroLink";
import { I18nContext } from "../../I18n/I18nProvider";
import { API_DATE_FORMAT } from "../../I18n/utils/date";
import { Loaders, QueryRenderer } from "../../QM/QueryRenderer";
import { PRIMARY_ROLE } from "../constants";
import { FormRow } from "../FormRow";
import { settingsMessages } from "../messages";
import {
  useCertificateOptions,
  useDeliveryFormatOptions,
  useDiagnosticToolOptions,
  useIndustryExperienceOptions,
  usePrimaryRolesOptions,
  useServiceTypeOptions,
  useTravelWillingnessOptions,
} from "../options";
import { WHITE_BG } from "../Settings.page";
import { useFieldsDict } from "../useFieldsDict";
import { CoachInfoFields } from "./CoachInfoFields";

const FIELDS = {
  firstName: "firstName",
  lastName: "lastName",
  email: "email", // nullable
  bio: "bio",
  webLink: "webLink", // nullable
  linkedinProfile: "linkedinProfile", // nullable
  languages: "languages",
  fields: "fields", // TODO: rename to topics
  certificate: "certificate",
  experienceSince: "experienceSince",
  publicProfile: "publicProfile",
  baseLocations: "baseLocations", // 10, multi, OTHER TODO!!
  travelWillingness: "travelWillingness", // 11, single, OTHER
  deliveryFormat: "deliveryFormat", // 12, multi, OTHER
  serviceType: "serviceType", // 13, multi, OTHER
  primaryRoles: "primaryRoles", // 14, TODO: move???
  // topics: "topics", // 15, TODO: rename "fields", add OTHER
  diagnosticTools: "diagnosticTools", // 16, multi, OTHER
  industryExperience: "industryExperience", // 17, multi, OTHER
  references: "references", // 18, STRING
};

const WITH_OTHER = {
  [FIELDS.fields]: { multi: true },
  [FIELDS.certificate]: { multi: true },
  [FIELDS.travelWillingness]: { multi: false },
  [FIELDS.deliveryFormat]: { multi: true },
  [FIELDS.serviceType]: { multi: true },
  [FIELDS.diagnosticTools]: { multi: true },
  [FIELDS.industryExperience]: { multi: true },
};

// react-hook-form does not allow "flat field array" https://react-hook-form.com/docs/usefieldarray
const listValueProp = "__value";

const FieldsList = ({
  name,
  placeholder = "",
  label = "",
  autoFocus = true,
  isRequired = true,
}) => {
  const generalMsg = useMsg({ dict: generalMessages });
  const { watch } = useFormContext();
  const { fields, append, remove } = useFieldArray({ name });
  const hasItems = watch(name)?.length > 0;
  const getName = (index) => `${name}.${index}.${listValueProp}`;
  const canDeleteRow = watch(name)?.length > 1;

  useEffect(() => {
    if (!hasItems) {
      // just for user-added, causes form.isDirty when used for init (dirtyFields -> baseLocations)
      append({ [listValueProp]: "" });
    }
  }, [append, hasItems]);

  return (
    <>
      {fields.map((field, index) => (
        <FormRow
          key={field.id}
          name={getName(index)}
          label={index === 0 && label}
          dividerTop={index === 0 && !!label} // when used with label, should look like separate field
          sx={{ mt: 2.5 }}
        >
          <BareInputField
            autoFocus={autoFocus}
            name={getName(index)}
            parametrizedValidate={[isRequired && ["required"]]}
            placeholder={placeholder}
          />
          {canDeleteRow && (
            <IconButton onClick={() => remove(index)}>
              <Icon name="Delete" />
            </IconButton>
          )}
        </FormRow>
      ))}
      <FormRow
        label={!hasItems && label}
        dividerTop={!hasItems}
        sx={{ mt: 2.5 }}
      >
        <Button onClick={() => append({ [listValueProp]: "" })}>
          {generalMsg("general.add")}
        </Button>
      </FormRow>
    </>
  );
};

// Special value affecting UI
export const OTHER_VALUE = "OTHER";

const getOtherName = (ownerName) => `_other_${ownerName}`;

const OtherField = ({ label = "", ownerName }) => {
  const otherValue = OTHER_VALUE;
  const { multi } = WITH_OTHER[ownerName];
  const { watch } = useFormContext();
  const ownerValue = watch(ownerName);
  const isOther = multi
    ? ownerValue?.includes(otherValue)
    : ownerValue === otherValue;
  const name = getOtherName(ownerName);

  if (multi && isOther) return <FieldsList name={name} />;

  return (
    <FormRow
      hidden={!isOther}
      name={name}
      label={label}
      dividerTop={false}
      sx={{ mt: 2.5 }}
    >
      <BareInputField
        autoFocus
        name={name}
        parametrizedValidate={[["required"]]}
      />
    </FormRow>
  );
};

const to = (data, { userLocale, userTz }) => {
  const experienceSince = parse(
    new Date(),
    API_DATE_FORMAT,
    data.experienceSince,
  );
  const initialValues = {
    ...data,
    // nullable fields affect dirty state, TODO: BE
    email: data.email || "",
    linkedinProfile: data.linkedinProfile || "",
    publicProfile: data.publicProfile,
    webLink: data.webLink || "",
    bio: data.bio || "",

    experienceSince: isValid(experienceSince) ? experienceSince : null,
    languages: data.languages?.length ? data.languages : [userLocale],
    baseLocations: data?.baseLocations?.map((value) => ({
      [listValueProp]: value,
    })) || [{ [listValueProp]: "" }], // affects dirtyFields
  };
  console.log("to", { initialValues, data });
  return initialValues;
};

const fromRHFList = (nestedValues) =>
  pipe(
    map(pipe(propOr("", listValueProp), trim)),
    filter(Boolean),
  )(nestedValues || []);

const resolveOther = ({ multi, ownerValue, otherValue }) => {
  if (!multi) return ownerValue === OTHER_VALUE ? otherValue : ownerValue;
  if (!ownerValue?.includes(OTHER_VALUE)) return ownerValue;
  return pipe(
    uniq,
    filter(Boolean),
    reject(equals(OTHER_VALUE)),
  )(concat(ownerValue || [], fromRHFList(otherValue)));
};

const resolveOtherFields = (values) => {
  return pipe(
    toPairs,
    map(([ownerName, { multi }]) => [
      ownerName,
      resolveOther({
        multi,
        ownerValue: values[ownerName],
        otherValue: values[getOtherName(ownerName)],
      }),
    ]),
    fromPairs,
  )(WITH_OTHER);
};

const from = (values, { userTz }) => {
  const { experienceSince, ...rest } = values;
  const payload = {
    ...rest,
    email: rest.email || null,
    linkedinProfile: rest.linkedinProfile || null,
    publicProfile: rest.publicProfile,
    webLink: rest.webLink || null,
    bio: rest.bio || null,
    experienceSince:
      experienceSince &&
      formatInTimeZone(API_DATE_FORMAT, userTz, experienceSince),
    baseLocations: fromRHFList(rest.baseLocations),
    ...resolveOtherFields(rest),
  };
  console.log("from", { values, payload });
  return payload;
};

export const useResetForm = ({ to = identity, form, initialResetting }) => {
  const [resetting, setResetting] = useState(initialResetting);
  const { reset } = form;
  const resetForm = useStaticCallback((data, options) => {
    reset(to(data), options);

    // Autocomplete needs remount after form reset, TODO
    setResetting(true);
    setTimeout(() => {
      setResetting(false);
    }, 0);
  });

  return {
    resetting,
    resetForm,
  };
};

// for Button
const getSaveButtonProps = ({
  isInitialized,
  isPending,
  isSaved,
  error,
  generalMsg,
}) => {
  return {
    disabled: !!error || isPending || !isInitialized,
    loading: isPending || !isInitialized,
    loadingPosition: "start",
    startIcon: <Icon name={isSaved ? "Check" : "ArrowUpward"} />,
    children: !isInitialized
      ? generalMsg("general.loading")
      : isSaved
      ? generalMsg("general.saved")
      : generalMsg("general.save"),
  };
};

const MOCK_EMPTY = {
  publicProfile: false,
  firstName: "matej",
  lastName: "matiasko",
  email: "matej.matiasko@gmail.com",
  webLink: null,
  bio: null,
  languages: [],
  fields: [],
  experienceSince: null,
  rate: null,
  rateOrder: null,
  internalRate: null,
  linkedinProfile: null,
  freeSlots: false,
  priority: 0,
  primaryRoles: null,
  certificate: null,
  baseLocations: null,
  travelWillingness: null,
  deliveryFormat: null,
  serviceType: null,
  topics: null,
  diagnosticTools: null,
  industryExperience: null,
  references: null,
  timeZone: "Europe/Prague",
};
const MOCK_WITH_OTHERS = {
  publicProfile: false,
  firstName: "matej",
  lastName: "matiasko",
  email: "matej.matiasko@gmail.com",
  webLink: null,
  bio: null,
  languages: [],
  fields: [],
  experienceSince: null,
  rate: null,
  rateOrder: null,
  internalRate: null,
  linkedinProfile: null,
  freeSlots: false,
  priority: 0,
  primaryRoles: null,
  certificate: ["PCC", "Some other cert"],
  baseLocations: ["Muang Phin", "Bangkok"],
  travelWillingness: "Sometimes",
  deliveryFormat: ["ONLINE", "From beach"],
  serviceType: ["Trialog"],
  topics: null,
  diagnosticTools: ["Sideric pendulum"],
  industryExperience: ["Agriculture"],
  references: null,
  timeZone: "Europe/Prague",
};

export const CoachProfileSettings = () => {
  const msg = useMsg({ dict: settingsMessages });
  const generalMsg = useMsg({ dict: generalMessages });
  const { authFetch, user } = useAuth();
  const { language, i18n, userTz } = useContext(I18nContext);
  const { fieldsOptions } = useFieldsDict();
  const certificatesOptions = useCertificateOptions();
  const travelWillingnessOptions = useTravelWillingnessOptions();
  const deliveryFormatOptions = useDeliveryFormatOptions();
  const serviceTypeOptions = useServiceTypeOptions();
  const primaryRolesOptions = usePrimaryRolesOptions();
  const diagnosticToolsOptions = useDiagnosticToolOptions();
  const industryExperienceOptions = useIndustryExperienceOptions();

  const initialValuesQuery = useMyQuery({
    queryKey: ["coach-info"],
    fetchDef: {
      url: "/api/latest/coach-info",
      // to: always(MOCK_WITH_OTHERS),
    },
  });
  const { data } = initialValuesQuery;
  const values = useMemo(
    () =>
      !data
        ? undefined
        : to(data, { userTz, userLocale: language.substring(0, 2) }),
    [data, language, userTz],
  );
  const form = useForm({
    mode: "onChange",
    defaultValues: values,
    values: values,
  });
  const primaryRoles = form.watch(FIELDS.primaryRoles);
  // Modified on success save by response:
  const defaultValues = useMemo(
    () => form.formState.defaultValues ?? {},
    [form.formState.defaultValues],
  );
  const { resetForm, resetting } = useResetForm({
    initialResetting: true,
    form,
  });
  // const formStateRef = useRef(form.formState);
  useEffect(() => {
    // console.log("%c[PS.eff reset]", "color:lime", {
    //   oldDefaultValues: formStateRef.current.defaultValues,
    //   newDefaultValues: values,
    //   isDirty: formStateRef.current.isDirty,
    // });
    if (values) {
      resetForm(values);
    }
  }, [values, resetForm]);

  const { getServerFieldErrorPairs, setServerFieldErrors } =
    useServerFieldErrors({ form });
  const setFocus = form.setFocus;
  const scrollToFieldName = useCallback(
    (fieldName) => {
      // TODO: move to RHForm -> TLForm
      if (!fieldName) return;
      const element = document.getElementsByName(fieldName)[0];
      if (element) {
        element.scrollIntoView({ behavior: "smooth", block: "center" });
        setFocus(fieldName);
      }
    },
    [setFocus],
  );

  const saveMutation = useMyMutation({
    fetchDef: {
      url: "/api/latest/coach-info",
      method: "POST",
      from: (values) => from(values, { userTz }),
    },
    // invalidate: { queryKey: ["coach-info"] }, // not needed as default values are reset on success
    onSuccess: (data) => {
      const newValues = to(data, {
        userTz,
        userLocale: language.substring(0, 2),
      });
      console.log("[save success]", { data });
      resetForm(newValues, {
        keepDirty: false,
      });
    },
  });

  const [reloadPhotoToken, reloadPhoto] = useReducer((i) => i + 1, 0);
  const postPhotoMutation = useMyMutation({
    mutationFn: async ({ file }) => {
      const formData = new FormData();
      formData.append("image", file, file.name);

      return authFetch({
        method: "POST",
        url: `/api/latest/coach-info/photo`,
        data: formData,
      });
    },
    onSuccess: () => {
      reloadPhoto();
    },
  });

  const handlePhotoUpload = (file) => {
    if (file) {
      postPhotoMutation.mutate({ file });
    }
  };

  const isJustLoaderDisplayed = initialValuesQuery.isPending || resetting;

  const isSaved = !form.formState.isDirty;
  const isPending = saveMutation.isPending;
  const hasInitialValuesError = !!initialValuesQuery.error;
  const handleSave = useStaticCallback((event) => {
    form
      .handleSubmit(saveMutation.mutateAsync, (errors, e) => {
        // not called on API error, just submission error
        const firstErrorKey = Object.keys(errors)[0]; // can be FieldArray name
        const firstError = errors?.[firstErrorKey];
        const firstErrorField = !Array.isArray(firstError)
          ? firstErrorKey
          : `${firstErrorKey}.${firstError.findIndex(
              Boolean,
            )}.${listValueProp}`; // TODO: do not depend on listValueProp
        console.log("[ProfileSettings.handleSubmit.onInvalid]", { errors, e });
        scrollToFieldName(firstErrorField);
      })(event)
      .then((data, ...other) => {
        // Called onInvalid. Not useful for form reset
        // debugger;
        return data;
      })
      .catch((error) => {
        // prevent unhandled rejection from mutation and propagate loading state. mutation.mutate is catching, but mutateAsync not
        // TODO: Encapsulate in Form
        // const jsonMaybe = [ { errorCode: "NotNull", fields: [ { name: "certificate", value: "null", }, ], errorMessage: "must not be null", }, { errorCode: "NotEmpty", fields: [ { name: "primaryRoles", value: "null", }, ], errorMessage: "must not be empty", }, ];
        const errorPairs = getServerFieldErrorPairs(error?.jsonMaybe);
        console.log("[ProfileSettings.handleSubmit.catch]", {
          error,
          errorPairs,
        });
        setServerFieldErrors(error?.jsonMaybe);
        scrollToFieldName(errorPairs[0]?.[0]);
      });
  });

  // console.log("%c[CoachProfileSettings.rndr]", "color:deepskyblue", {
  //   form,
  //   isJustLoaderDisplayed,
  //   isDirty: form.formState.isDirty,
  //   dirtyFields: form.formState.dirtyFields,
  //   defaultValues: form.formState.defaultValues,
  //   currentValues: form.watch(),
  // });

  useRightMenu(
    useMemo(
      () => (
        <ScrollableRightMenu
          heading={msg("settings.profile.aside.title")}
          buttonProps={{
            type: "submit",
            ...getSaveButtonProps({
              isInitialized: !isJustLoaderDisplayed,
              isPending,
              isSaved,
              error: hasInitialValuesError,
              generalMsg,
            }),
            onClick: handleSave,
          }}
        >
          <Box
            sx={{
              borderRadius: 0.6,
              minWidth: 225,
              width: 225,
              position: "relative",
              overflow: "hidden",
            }}
          >
            <Box
              component="img"
              sx={{
                width: "100%",
                height: "100%",
                borderRadius: 1,
              }}
              alignSelf={"center"}
              src={`/api/latest/coach-info/photo?${reloadPhotoToken}`}
            />
            <IntroLink webLink={defaultValues.webLink} />
          </Box>
          <CoachInfoFields msg={msg} i18n={i18n} coach={defaultValues} />
        </ScrollableRightMenu>
      ),
      [
        msg,
        isJustLoaderDisplayed,
        isPending,
        isSaved,
        generalMsg,
        hasInitialValuesError,
        handleSave,
        reloadPhotoToken,
        defaultValues,
        i18n,
      ],
    ),
  );

  if (initialValuesQuery.error)
    return (
      <QueryRenderer
        error={initialValuesQuery.error}
        errored={() => <Alert severity="error">Profile fetch failed</Alert>}
      />
    );

  // reset of MUI Autocomplete
  if (isJustLoaderDisplayed) return <Loaders.CircularBlock spaced />;

  return (
    <FormProvider {...form}>
      <H2 gutterBottom>
        <Msg id="settings.profile.heading" />
      </H2>
      <P sx={{ mb: -1 }}>
        <Msg id="settings.profile.perex" />
      </P>
      <FormRow
        label={msg("settings.profile.field.publicProfile")}
        name={FIELDS.publicProfile}
      >
        <Box display="flex">
          <SwitchField name={FIELDS.publicProfile} />
        </Box>
      </FormRow>
      <FormRow
        label={msg("settings.profile.field.name")}
        name={FIELDS.lastName}
      >
        <BareInputField
          name={FIELDS.firstName}
          parametrizedValidate={[
            ["required"],
            ["notBlank"],
            ["minLength", { gteLength: 2 }],
          ]}
        />
        <BareInputField
          name={FIELDS.lastName}
          parametrizedValidate={[
            ["required"],
            ["notBlank"],
            ["minLength", { gteLength: 2 }],
          ]}
        />
      </FormRow>

      <FormRow label={msg("settings.profile.field.email")} name={FIELDS.email}>
        <BareInputField
          name={FIELDS.email}
          autoComplete="email"
          parametrizedValidate={[["required"], ["email"]]}
        />
      </FormRow>

      <FormRow label={msg("settings.profile.field.photo")}>
        <FileUpload
          src={`/api/latest/coach-info/photo`}
          secondaryText={msg("settings.profile.field.photo.limit", {
            maxSize: "10MB",
          })}
          onChange={handlePhotoUpload}
        />
      </FormRow>

      <FormRow label={msg("settings.profile.field.bio")} name={FIELDS.bio}>
        <BareInputField
          name={FIELDS.bio}
          multiline
          rows={4}
          parametrizedValidate={[["maxLength", { lteLength: 1000 }]]}
        />
      </FormRow>
      <FormRow
        label={msg("settings.profile.field.webLink")}
        name={FIELDS.webLink}
      >
        <BareInputField
          name={FIELDS.webLink}
          placeholder={msg("settings.profile.field.webLink.placeholder")}
        />
      </FormRow>
      <FormRow
        label={msg("settings.profile.field.linkedinProfile")}
        name={FIELDS.linkedinProfile}
      >
        <BareInputField
          name={FIELDS.linkedinProfile}
          placeholder={msg(
            "settings.profile.field.linkedinProfile.placeholder",
          )}
        />
      </FormRow>

      <FormRow
        label={msg("settings.profile.field.languages")}
        name={FIELDS.languages}
      >
        <AutocompleteSelect
          multiple
          sx={WHITE_BG}
          name={FIELDS.languages}
          options={getCoachLanguagesOptions()}
          // renderOption={renderLanguageOption}
          placeholder={msg("settings.profile.field.languages.placeholder")}
          enableIsOptionEqualToValue
        />
      </FormRow>

      <H2 gutterBottom sx={{ mt: 4 }}>
        <Msg id="settings.profile.expertise" />
      </H2>

      <FormRow
        label={msg("settings.profile.field.fields")}
        name={FIELDS.fields}
      >
        <AutocompleteSelect
          multiple
          sx={WHITE_BG}
          name={FIELDS.fields}
          options={fieldsOptions}
          placeholder={msg("settings.profile.field.fields.placeholder")}
          enableIsOptionEqualToValue
        />
      </FormRow>
      <OtherField ownerName={FIELDS.fields} />

      <FormRow
        label={msg("settings.profile.field.certificates")}
        name={FIELDS.certificate}
      >
        <AutocompleteSelect
          multiple
          sx={WHITE_BG}
          name={FIELDS.certificate}
          options={certificatesOptions}
          placeholder={msg("settings.profile.field.certificates.placeholder")}
          parametrizedValidate={[
            primaryRoles?.includes(PRIMARY_ROLE.COACH) && ["required"],
          ]}
          triggerRevalidationToken={JSON.stringify(primaryRoles)}
        />
      </FormRow>
      <OtherField ownerName={FIELDS.certificate} />
      <FormRow
        label={msg("settings.profile.field.experience")}
        name={FIELDS.experienceSince}
      >
        <DatePickerField
          name={FIELDS.experienceSince}
          textFieldProps={{ sx: { ...WHITE_BG, width: "100%" } }}
          parametrizedValidate={[["required"], ["validDate"]]}
          size="small"
        />
      </FormRow>

      <FieldsList
        label={msg("settings.profile.field.baseLocations")}
        placeholder={msg("settings.profile.field.baseLocations.placeholder")}
        name={FIELDS.baseLocations}
        autoFocus={false}
        isRequired={false}
      />
      <FormRow
        label={msg("settings.profile.field.travelWillingness")}
        name={FIELDS.travelWillingness}
      >
        {/* 11 */}
        <AutocompleteSelect
          sx={WHITE_BG}
          name={FIELDS.travelWillingness}
          options={travelWillingnessOptions}
          enableIsOptionEqualToValue
        />
      </FormRow>
      <OtherField ownerName={FIELDS.travelWillingness} />
      <FormRow
        label={msg("settings.profile.field.deliveryFormat")}
        name={FIELDS.deliveryFormat}
      >
        {/* 12 */}
        <AutocompleteSelect
          multiple
          sx={WHITE_BG}
          name={FIELDS.deliveryFormat}
          options={deliveryFormatOptions}
          enableIsOptionEqualToValue
        />
      </FormRow>
      <OtherField ownerName={FIELDS.deliveryFormat} />
      <FormRow
        label={msg("settings.profile.field.serviceType")}
        name={FIELDS.serviceType}
      >
        {/* 13 */}
        <AutocompleteSelect
          multiple
          sx={WHITE_BG}
          name={FIELDS.serviceType}
          options={serviceTypeOptions}
          enableIsOptionEqualToValue
        />
      </FormRow>
      <OtherField ownerName={FIELDS.serviceType} />
      <FormRow
        label={msg("settings.profile.field.primaryRoles")}
        name={FIELDS.primaryRoles}
      >
        <AutocompleteSelect
          multiple
          sx={WHITE_BG}
          name={FIELDS.primaryRoles}
          options={primaryRolesOptions}
          enableIsOptionEqualToValue
        />
      </FormRow>

      {/* TODO: fields -> topics here??? */}

      <FormRow
        label={msg("settings.profile.field.diagnosticTools")}
        name={FIELDS.diagnosticTools}
      >
        {/* 16 */}
        <AutocompleteSelect
          multiple
          sx={WHITE_BG}
          name={FIELDS.diagnosticTools}
          options={diagnosticToolsOptions}
          enableIsOptionEqualToValue
        />
      </FormRow>
      <OtherField ownerName={FIELDS.diagnosticTools} />
      <FormRow
        label={msg("settings.profile.field.industryExperience")}
        name={FIELDS.industryExperience}
      >
        {/* 17 */}
        <AutocompleteSelect
          multiple
          sx={WHITE_BG}
          name={FIELDS.industryExperience}
          options={industryExperienceOptions}
          enableIsOptionEqualToValue
        />
      </FormRow>
      <OtherField ownerName={FIELDS.industryExperience} />
      <FormRow
        label={msg("settings.profile.field.references")}
        name={FIELDS.references}
      >
        {/* 18 */}
        <BareInputField
          name={FIELDS.references}
          placeholder={msg("settings.profile.field.references.placeholder")}
          multiline
          rows={5}
          parametrizedValidate={[["maxLength", { lteLength: 2000 }]]}
        />
      </FormRow>
    </FormProvider>
  );
};
